import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { wsUrl } from "@/lib/api";
import { toast } from "@/lib/toast";
import { getToken } from "@/lib/session";

/**
 * <NotificationToaster> — mounts once globally inside the dashboard shell.
 * Opens a WebSocket to /api/ws/notifications using the current user's session
 * token, listens for {type:'notification', data:{...}} pushes, and shows a
 * brand-styled toast for each one. The bell badge auto-refreshes itself
 * on the next render cycle via the existing polling.
 *
 * On disconnect we exponential-backoff reconnect up to 30s (capped) so
 * SXM cell drops don't kill the channel forever.
 */
export default function NotificationToaster() {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let timer = null;

    const connect = () => {
      if (cancelled) return;
      const token = getToken();
      if (!token) return;
      const ws = new WebSocket(wsUrl(`/api/ws/notifications?token=${encodeURIComponent(token)}`));
      wsRef.current = ws;
      ws.onopen = () => { retryRef.current = 0; };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type !== "notification" || !msg.data) return;
          const n = msg.data;
          toast.message(n.title || "New notification", {
            description: n.body,
            duration: 6000,
            action: n.link ? {
              label: "Open",
              onClick: () => { window.location.assign(n.link); },
            } : undefined,
          });
          // Trigger a re-fetch of the bell count by dispatching a custom event
          window.dispatchEvent(new CustomEvent("pb:notification"));
        } catch (err) {
          console.warn("NotificationToaster parse fail:", err);
        }
      };
      ws.onclose = () => {
        if (cancelled) return;
        // exponential backoff capped at 30s
        const delay = Math.min(30_000, 1000 * 2 ** retryRef.current);
        retryRef.current = Math.min(5, retryRef.current + 1);
        timer = setTimeout(connect, delay);
      };
      ws.onerror = () => { /* will fall through to onclose */ };
    };
    // Defer one tick so React StrictMode / fast nav unmounts don't fire
    // 'closed before established' warnings during the WebSocket upgrade.
    const startTimer = setTimeout(connect, 0);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (timer) clearTimeout(timer);
      const ws = wsRef.current;
      if (ws) {
        // Only call close once the socket has actually opened, otherwise the
        // browser logs a noisy "WebSocket is closed before the connection
        // is established" warning.
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.close(); } catch { /* ignore */ }
        } else if (ws.readyState === WebSocket.CONNECTING) {
          ws.addEventListener("open", () => { try { ws.close(); } catch { /* ignore */ } }, { once: true });
        }
      }
    };
  }, [user?.user_id]); // eslint-disable-line

  return null;
}
